import { Button } from 'components/button/Button';
import { InputField } from 'components/input/InputField';
import { Formik } from 'components/form/formik';
import diff from 'object-diff';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { useSWRConfig } from 'swr';
import * as Yup from 'yup';

import { updateVatRate, useVatRatesSuspense, VAT_RATES_SWR_KEY } from '../models';
import { getDisplayError } from '../../../utils/get-display-error';
import { nullthrows } from '../../../utils/invariant';
import { decimateAndFormatNumber, parseNumberInput } from '../../../utils/number';
import { captureException } from '@sentry/react';
import { DelayedSpinner } from 'components/Spinner';

type VatRateItem = NonNullable<ReturnType<typeof useVatRatesSuspense>['data']>[number];

const createVatRateSchema = Yup.object().shape({
  id: Yup.string().required('Vereist'),
  amount: Yup.string().required('Vereist'),
  description: Yup.string().required('Vereist'),
});

interface IVatRateValues {
  id: string;
  amount: string;
  description: string;
}

interface IUpdateVatRateFormProps {
  id: string;
  onComplete?: (values: { id: string; amount: number; description: string }) => void;
  onCancel?: () => void;
}

export const UpdateVatRateForm: React.FC<IUpdateVatRateFormProps> = (props) => {
  const { id, onComplete, onCancel } = props;
  const { data, isLoading } = useVatRatesSuspense();

  if (isLoading) {
    return <DelayedSpinner size={8} className="flex justify-center items-center p-8" />;
  }

  const vatRate = nullthrows(
    data?.find((v) => v.id === id),
    'Vat rate not found',
  );
  return <VatRateForm vatRate={vatRate} onComplete={onComplete} onCancel={onCancel} />;
};

const VatRateForm: React.FC<{
  vatRate: VatRateItem;
  onComplete?: (values: { id: string; amount: number; description: string }) => void;
  onCancel?: () => void;
}> = ({ vatRate, onComplete, onCancel }) => {
  const { mutate } = useSWRConfig();

  const initialValues: IVatRateValues = useMemo(() => {
    return {
      id: vatRate.id,
      amount: decimateAndFormatNumber(vatRate.amount, 2),
      description: vatRate.description,
    };
  }, [vatRate]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={createVatRateSchema}
      onSubmit={async (values) => {
        try {
          const patch: Partial<typeof values> = diff(initialValues, values);
          await updateVatRate(vatRate.id, {
            id: patch.id,
            description: patch.description,
            amount: patch.amount ? parseNumberInput(patch.amount, 2) : undefined,
          });
          await mutate(VAT_RATES_SWR_KEY);
          onComplete?.({
            ...values,
            amount: parseNumberInput(values.amount, 2),
          });
          toast.success('btw type aangepast');
        } catch (err: any) {
          captureException(err);
          toast.error('Kon btw type niet aanpassen: ' + getDisplayError(err));
        }
      }}
    >
      {({ handleSubmit, isSubmitting, submitForm }) => {
        return (
          <form onSubmit={handleSubmit}>
            <InputField labelText="Code" type="text" name="id" />
            <InputField labelText="Percentage" type="text" name="amount" />
            <InputField labelText="Beschrijving" type="text" name="description" />

            <div className="flex justify-between">
              {onCancel && (
                <div>
                  <Button isDisabled={isSubmitting} isLoading={isSubmitting} onTrigger={onCancel}>
                    Annuleer
                  </Button>
                </div>
              )}
              <Button
                type="submit"
                color="primary"
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                onTrigger={submitForm}
              >
                Pas btw type aan
              </Button>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};
